import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const AddAgent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    agent_id: '',
    name: '',
    description: '',
    endpoint: '',
    facts_url: '',
    specialization: '',
    capabilities: '',
    expertise: '',
    agent_type: 'skill',
    status: 'running'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages on input change
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.agent_id || !formData.name || !formData.endpoint) {
        setError('Agent ID, Name, and Endpoint are required fields');
        setLoading(false);
        return;
      }

      // Parse arrays from comma-separated strings
      const agentData = {
        agent_id: formData.agent_id.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || `AI-powered ${formData.name} agent`,
        endpoint: formData.endpoint.trim(),
        a2a_endpoint: formData.endpoint.trim() + '/a2a',
        facts_url: formData.facts_url.trim() || `https://list39.org/@${formData.agent_id}.json`,
        agentFactsURL: formData.facts_url.trim() || `https://list39.org/@${formData.agent_id}.json`,
        specialization: formData.specialization.trim() || formData.name + ' specialization',
        capabilities: formData.capabilities ? 
          formData.capabilities.split(',').map(c => c.trim()).filter(Boolean) : 
          ['General AI Agent'],
        expertise: formData.expertise ? 
          formData.expertise.split(',').map(e => e.trim()).filter(Boolean) : 
          [formData.specialization || 'General AI'],
        agent_type: formData.agent_type,
        status: formData.status,
        created_at: new Date().toISOString(),
        last_seen: new Date().toISOString(),
        interaction_stats: {
          total_ratings_received: 0,
          average_rating: 0,
          total_interactions: 0
        }
      };

      // Use relative path for API call
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(`${apiBase}/agents`, agentData, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess('Agent created successfully! Redirecting...');
        setTimeout(() => {
          navigate('/agents');
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating agent:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={() => navigate('/agents')}
          className="btn btn-secondary"
          style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <ArrowLeft size={16} />
          Back to Agents
        </button>
        <h1 className="page-title">Add New Agent</h1>
        <p className="page-subtitle">Register a new agent to the NANDA network</p>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#22c55e'
        }}>
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#ef4444'
        }}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="card" style={{ padding: '32px' }}>
        {/* Agent Username (Agent ID) */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Agent Username <span style={{ color: '#ef4444' }}>*</span>
            <span style={{ 
              color: 'var(--text-secondary)', 
              fontWeight: '400',
              fontSize: '0.8125rem',
              marginLeft: '8px'
            }}>
              (Agent ID)
            </span>
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--accent)',
              fontFamily: 'monospace',
              fontSize: '0.9rem'
            }}>@</span>
            <input
              type="text"
              name="agent_id"
              value={formData.agent_id}
              onChange={handleChange}
              placeholder="maria-agent"
              className="input"
              required
              style={{ width: '100%', paddingLeft: '32px', fontFamily: 'monospace' }}
            />
          </div>
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Unique username for the agent (lowercase, hyphens allowed). Will be displayed as @username
          </small>
        </div>

        {/* Name */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Agent Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Tech Expert, Financial Advisor"
            className="input"
            required
            style={{ width: '100%' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Display name for the agent
          </small>
        </div>

        {/* Description */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of what this agent does..."
            className="input"
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Brief description (will be auto-generated if left empty)
          </small>
        </div>

        {/* Endpoint */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Agent Endpoint <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="url"
            name="endpoint"
            value={formData.endpoint}
            onChange={handleChange}
            placeholder="http://54.172.221.69:6000"
            className="input"
            required
            style={{ width: '100%' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Full URL where the agent is running (including port)
          </small>
        </div>

        {/* AgentFacts URL */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            AgentFacts URL
          </label>
          <input
            type="url"
            name="facts_url"
            value={formData.facts_url}
            onChange={handleChange}
            placeholder="https://list39.org/@username.json"
            className="input"
            style={{ width: '100%' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Link to agent's profile (will be auto-generated if left empty)
          </small>
        </div>

        {/* Specialization */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Specialization
          </label>
          <input
            type="text"
            name="specialization"
            value={formData.specialization}
            onChange={handleChange}
            placeholder="e.g., Technology and Software Development"
            className="input"
            style={{ width: '100%' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Primary area of expertise
          </small>
        </div>

        {/* Capabilities */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Capabilities
          </label>
          <input
            type="text"
            name="capabilities"
            value={formData.capabilities}
            onChange={handleChange}
            placeholder="AI, Software Development, Cloud Computing"
            className="input"
            style={{ width: '100%' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Comma-separated list of capabilities
          </small>
        </div>

        {/* Expertise */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Expertise
          </label>
          <input
            type="text"
            name="expertise"
            value={formData.expertise}
            onChange={handleChange}
            placeholder="Machine Learning, API Development, DevOps"
            className="input"
            style={{ width: '100%' }}
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Comma-separated list of expertise areas
          </small>
        </div>

        {/* Agent Type */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Agent Type
          </label>
          <select
            name="agent_type"
            value={formData.agent_type}
            onChange={handleChange}
            className="input"
            style={{ width: '100%' }}
          >
            <option value="skill">Skill Agent</option>
            <option value="persona">Persona Agent</option>
            <option value="communication">Communication Agent</option>
            <option value="mcp">MCP Server</option>
          </select>
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Category of agent
          </small>
        </div>

        {/* Status */}
        <div style={{ marginBottom: '32px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '0.9rem'
          }}>
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="input"
            style={{ width: '100%' }}
          >
            <option value="running">Running (Online)</option>
            <option value="offline">Offline</option>
            <option value="maintenance">Maintenance</option>
          </select>
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Current operational status
          </small>
        </div>

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/agents')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {loading ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Creating...
              </>
            ) : (
              <>
                <Plus size={16} />
                Create Agent
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAgent;

